import { BidPaymentModel, BidPointViewModel, DistributionBidPaymentType, MapObjectType } from "api";
import {
    bidPointTypesEnum,
    distributionBidOptions,
    distributionBidOptionsEnum,
    mapObjectTypesEnum,
    syncEntityTypesEnum,
} from "helpers/enums";
import { OdataFilters, queryCollection, queryParams } from "helpers/reqparams";
import { flagVal } from "helpers/system";
import { alertRequest, body, request } from "services/request";
import { getLastSyncStatus } from "sources/syncStatuses";

const isPreBidFlagVal = flagVal(distributionBidOptions, distributionBidOptionsEnum.IsPreBid);

const resource = "api/distributionBids";
const { protocol, host } = location;
const url = `${protocol}//${host}`;
const distributionBidsQuery = {
    orderBy: ["id desc"],
};

export const index = ({ preparedFilter, queryFilter, ...params }: OdataFilters) => {
    const qp = new URLSearchParams(queryFilter as URLSearchParams);
    const query = queryCollection(params, { filter: preparedFilter, ...distributionBidsQuery }).substr(1);
    const queryString = [qp.toString(), query].filter((item) => item.length).join("&");
    return request(`${resource}/getlist?${queryString}`, { method: "GET" }, { countInHeaders: true });
};

export const apply = (params: {}) => alertRequest(`${resource}/apply`, body(params));

export const getItem = (id: number) => {
    return request(`${resource}/get/${id}`, { method: "GET" }).then((data) => {
        data.bidPoints.forEach((point: BidPointViewModel & { counterpartyId: number | undefined }) => {
            if (point.type === bidPointTypesEnum.CustomPoint) {
                point.customPointTypeId = point.customPointType!.id;
            }
            point.counterpartyId = point.counterparty?.id;
        });
        data.contractId = data.contract?.id;
        data.intOptions = data.isPreBid ? isPreBidFlagVal : 0;
        return data;
    });
};
export const getListOfExternalForPlanning = () =>
    request(`${resource}/GetListOfExternalForPlanning`, { method: "GET" });
export const getCountOfAcceptedExternalForPlanning = () =>
    request(`${resource}/getCountOfAcceptedExternalForPlanning`, { method: "GET" });
export const convertToBid = (params: {}) => alertRequest(`${resource}/convertToBid`, body(params));

export const getMatchingForCar = ({
    carId,
    preparedFilter,
    queryFilter,
    ...params
}: OdataFilters & { carId: number }) => {
    const qp = new URLSearchParams(queryFilter as URLSearchParams);
    const query = queryCollection(
        params,
        { filter: preparedFilter, orderBy: ["emptyMileage asc, outputWithEmptyMileage desc"] },
        false,
    ).substr(1);
    const queryString = [`carId=${carId}`, qp.toString(), query].filter((item) => item.length).join("&");
    return request(`${resource}/getMatchingForCar?${queryString}`, { method: "GET" }).then((data) =>
        data ? { data } : data,
    );
};

export const getForBid = (id: number) => {
    return request(`${resource}/getForBid/${id}`, { method: "GET" }).then((data) => {
        data.bidPoints.forEach((point: BidPointViewModel) => {
            if (point.type === bidPointTypesEnum.CustomPoint) {
                point.customPointTypeId = point.customPointType!.id;
            }
            if (point.geozone!.type === mapObjectTypesEnum.CounterpartyPoint) {
                point.geozone!.type = mapObjectTypesEnum.BidPoint as MapObjectType;
            }
            data.contractId = data.contract?.id;
            data.cargoOwnerDictionaryItemId = data.counterparty?.id;
        });
        return data;
    });
};

export const getDistributionBid = (id: number) =>
    Promise.all([getItem(id), getLastSyncStatus(syncEntityTypesEnum.DistributionBid, id)]).then(
        ([distributionBid, syncStatusData]) => (distributionBid ? { ...distributionBid, syncStatusData } : false),
    );
export const acceptExternalDistributionBid = (params: {}) => alertRequest(`${resource}/AcceptExternal`, body(params));
export const declineExternalDistributionBid = (params: {}) => alertRequest(`${resource}/DeclineExternal`, body(params));
export const deleteItem = (id: number) => alertRequest(`${resource}/delete`, body({ id }));
export const cancel = (params: {}) => alertRequest(`${resource}/cancel`, body(params));
export const clone = (params: {}) => alertRequest(`${resource}/clone`, body(params));
export const restore = (params: {}) => alertRequest(`${resource}/restore`, body(params));
export const revert = (params: {}) => alertRequest(`${resource}/revert`, body(params));
export const addFile = (params: {}) => request(`${resource}/addFile`, params);
export const deleteFile = (params: {}) => alertRequest(`${resource}/deleteFile`, body(params));
export const getFiles = (id: number) => request(`${resource}/getFiles/${id}`, { method: "GET" });
type SetInProcessStatusParams = {
    id: number | string;
    isInProcess: boolean;
};

type ReleaseInProcessStatusOptions = {
    keepalive?: boolean;
};

export const setInProcessStatus = (params: SetInProcessStatusParams) =>
    request(`${resource}/setInProcessStatus`, body(params), { withoutErrorAlert: true });

export const resetInProcessStatus = (id: number | string) =>
    alertRequest(`${resource}/setInProcessStatus`, body({ id, isInProcess: false }));

export const releaseInProcessStatus = (id: number | string, options: ReleaseInProcessStatusOptions = {}) =>
    fetch(`${url}/${resource}/setInProcessStatus`, {
        method: "POST",
        cache: "default",
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            "X-Device-Id": localStorage.getItem("deviceId"),
            "Content-Type": "application/json; charset=utf-8",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ id, isInProcess: false }),
        keepalive: options.keepalive,
    } as RequestInit)
        .then((res) => res.ok)
        .catch(() => false);
export const createInvoice = (params: {}) => alertRequest(`${resource}/createInvoice`, body(params));
export const createAcceptanceCertificate = (params: {}) =>
    alertRequest(`${resource}/createAcceptanceCertificate`, body(params));
export const createAcceptanceCertificateInvoice = (params: {}) =>
    alertRequest(`${resource}/createTaxInvoice`, body(params));
export const createDocumentFromTemplate = (params: {}) =>
    alertRequest(`${resource}/createDocumentFromTemplate`, body(params));
export const createHiredCounterpartyDocumentFromTemplate = (params: {}) =>
    alertRequest(`${resource}/createHiredCounterpartyDocumentFromTemplate`, body(params));
export const applyPayment = (params: {
    distributionBidId: number;
    paymentType: DistributionBidPaymentType;
    payment: BidPaymentModel;
}) => alertRequest(`${resource}/applyPayment`, body(params));
export const getPaymentChanges = (distributionBidId: number, paymentType: DistributionBidPaymentType) =>
    request(`${resource}/getPaymentChanges${queryParams({ distributionBidId, paymentType })}`, { method: "GET" });
export const getClassificationTypePreview = ({ id, ...params }: { id: number }) =>
    request(`${resource}/getClassificationTypePreview${queryParams({ distributionBidId: id, ...params })}`, {
        method: "GET",
    });
